import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { resourcesService, Resource, ResourceTag, ResourceFilter } from '@/services/resources';
import { ResourceFormModal } from '@/components/resources/ResourceFormModal';
import { ResourcePreviewModal } from '@/components/resources/ResourcePreviewModal';
import { ResourceCard, ViewSwitcher, CardGrid, ViewMode, FilterToolbar } from '@/components/cards';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  DocumentIcon,
  TagIcon
} from '@heroicons/react/24/outline';

// =====================================================
// RESOURCES & KNOWLEDGE BASE PAGE
// TMOS Platform - Main Resources Page
// =====================================================
// Component: ResourcesPage
// Description: Main page for Resources & Knowledge Base System
// Author: TMOS Development Team
// Date: September 30, 2025
// =====================================================

export const ResourcesPage: React.FC = () => {
  const { t } = useLanguage();
  const [resources, setResources] = useState<Resource[]>([]);
  const [tags, setTags] = useState<ResourceTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<'all' | 'link' | 'document'>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  
  // Card view state
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('resources-view-mode');
    return (saved === 'cards' || saved === 'table') ? saved : 'cards';
  });
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('relevance');

  // =====================================================
  // UTILITY FUNCTIONS
  // =====================================================

  const deduplicateResources = (resources: Resource[]): Resource[] => {
    const uniqueResources = resources.filter((resource, index, self) => 
      index === self.findIndex(r => r.id === resource.id)
    );
    
    // Debug: Log if duplicates were found
    if (resources.length !== uniqueResources.length) {
      console.warn(`Found ${resources.length - uniqueResources.length} duplicate resources`);
    }
    
    return uniqueResources;
  };

  // =====================================================
  // DATA LOADING
  // =====================================================

  useEffect(() => {
    loadData();
  }, []);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('resources-view-mode', viewMode);
  }, [viewMode]);

  // Apply filters and sorting to resources
  useEffect(() => {
    if (viewMode === 'cards') {
      applyFiltersAndSort();
    }
  }, [resources, searchTerm, selectedType, selectedTags, activeFilters, sortBy, viewMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [resourcesData, tagsData] = await Promise.all([
        resourcesService.getResources(),
        resourcesService.getTags()
      ]);

      const uniqueResources = deduplicateResources(resourcesData.resources);
            console.log('Resource IDs:', uniqueResources.map(r => r.id));
      setResources(uniqueResources);
      setTags(tagsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FILTERING AND SEARCH
  // =====================================================

  const applyFiltersAndSort = () => {
    let filtered = [...resources];

    // Apply search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(resource =>
        resource.title?.toLowerCase().includes(searchLower) ||
        resource.description?.toLowerCase().includes(searchLower) ||
        resource.tags?.some(tag => tag.name.toLowerCase().includes(searchLower))
      );
    }

    // Apply type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(resource => resource.resource_type === selectedType);
    }

    // Apply tag filters
    if (selectedTags.length > 0) {
      filtered = filtered.filter(resource =>
        resource.tags?.some(tag => selectedTags.includes(tag.id))
      );
    }

    // Apply additional active filters
    if (activeFilters.length > 0) {
      filtered = filtered.filter(resource => {
        if (activeFilters.includes('featured') && !resource.featured_in_widget) return false;
        if (activeFilters.includes('active') && !resourcesService.isResourceActive(resource)) return false;
        if (activeFilters.includes('links') && resource.resource_type !== 'link') return false;
        if (activeFilters.includes('documents') && resource.resource_type !== 'document') return false;
        return true;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'title':
        filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'date-newest':
        filtered.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        break;
      case 'date-oldest':
        filtered.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
        break;
      case 'views':
        filtered.sort((a, b) => (b.access_count || 0) - (a.access_count || 0));
        break;
      case 'relevance':
      default:
        // Smart defaults: featured first, then by access count, then by date
        filtered.sort((a, b) => {
          const aFeatured = a.featured_in_widget ? 1 : 0;
          const bFeatured = b.featured_in_widget ? 1 : 0;
          if (aFeatured !== bFeatured) return bFeatured - aFeatured;
          const aViews = a.access_count || 0;
          const bViews = b.access_count || 0;
          if (aViews !== bViews) return bViews - aViews;
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        });
        break;
    }

    setFilteredResources(filtered);
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: ResourceFilter = {
        search_term: searchTerm || undefined,
        resource_type: selectedType === 'all' ? undefined : selectedType,
        tag_ids: selectedTags.length > 0 ? selectedTags : undefined,
        active_only: true
      };

      const result = await resourcesService.getResources(filters);
      const uniqueResults = deduplicateResources(result.resources);
      setResources(uniqueResults);
    } catch (err: any) {
      setError(err.message || 'Failed to search resources');
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tagId: string) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedTags([]);
    loadData();
  };

  // =====================================================
  // RESOURCE ACTIONS
  // =====================================================

  const handleResourceClick = (resource: Resource) => {
    setSelectedResource(resource);
    setShowPreviewModal(true);
  };

  const handleEditResource = (resource: Resource) => {
    setSelectedResource(resource);
    setShowCreateModal(true);
  };

  const handleDeleteResource = async (resource: Resource) => {
    if (window.confirm(`Are you sure you want to delete "${resource.title}"?`)) {
      try {
        await resourcesService.deleteResource(resource.id);
        await loadData();
      } catch (err: any) {
        setError(err.message || 'Failed to delete resource');
      }
    }
  };

  // =====================================================
  // RENDER HELPERS
  // =====================================================

  // =====================================================
  // RENDER
  // =====================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="bg-white rounded-lg shadow-sm border border-[var(--color-border)] p-4">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="bg-white rounded-lg shadow-sm border border-[var(--color-border)] p-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
              <button
                onClick={loadData}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-white rounded-lg shadow-sm border border-[var(--color-border)] p-4">
          {/* Header */}
          <div className="mb-2">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl font-bold text-[var(--color-surface-text)] mb-1">
                  {t('pages.resources.title')}
                </h1>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {t('pages.resources.description')}
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <PlusIcon className="h-5 w-5" />
                <span>{t('pages.resources.addResource')}</span>
              </button>
            </div>
          </div>

      {/* Legacy Search and Filters (for table view) */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('pages.resources.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Type Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">{t('pages.resources.filterByType')}:</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as 'all' | 'link' | 'document')}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t('pages.resources.allTypes')}</option>
                <option value="link">{t('pages.resources.links')}</option>
                <option value="document">{t('pages.resources.documents')}</option>
              </select>
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
              <span>{t('common.search')}</span>
            </button>
          </div>

          {/* Tags Filter */}
          {tags.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center space-x-2 mb-2">
                <TagIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">{t('pages.resources.filterByTags')}:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, tagIndex) => (
                  <button
                    key={`filter-tag-${tag.id}-${tagIndex}`}
                    onClick={() => handleTagToggle(tag.id)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedTags.includes(tag.id)
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Clear Filters */}
          {(searchTerm || selectedType !== 'all' || selectedTags.length > 0) && (
            <div className="mt-4">
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* View Switcher and Filter Toolbar */}
      <div className="mb-4 flex justify-between items-center">
        {viewMode === 'cards' && (
          <FilterToolbar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder={t('pages.resources.searchPlaceholder') || 'Search resources...'}
            quickFilters={[
              { id: 'featured', label: 'Featured', value: 'featured' },
              { id: 'active', label: 'Active', value: 'active' },
              { id: 'links', label: 'Links', value: 'links' },
              { id: 'documents', label: 'Documents', value: 'documents' }
            ]}
            activeFilters={activeFilters}
            onFilterToggle={(filterId) => {
              setActiveFilters(prev =>
                prev.includes(filterId)
                  ? prev.filter(id => id !== filterId)
                  : [...prev, filterId]
              );
            }}
            sortOptions={[
              { id: 'relevance', label: 'Relevance', value: 'relevance' },
              { id: 'title', label: 'Title (A-Z)', value: 'title' },
              { id: 'date-newest', label: 'Date (Newest)', value: 'date-newest' },
              { id: 'date-oldest', label: 'Date (Oldest)', value: 'date-oldest' },
              { id: 'views', label: 'Most Viewed', value: 'views' }
            ]}
            sortValue={sortBy}
            onSortChange={setSortBy}
          />
        )}
        <ViewSwitcher
          currentView={viewMode}
          onViewChange={setViewMode}
        />
      </div>

      {/* Resources Display */}
      {viewMode === 'cards' ? (
        <div>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75">
                {resources.length === 0
                  ? 'No resources found'
                  : 'No resources match your filters'}
              </p>
            </div>
          ) : (
            <CardGrid columns={4}>
              {filteredResources.map((resource) => (
                <ResourceCard
                  key={resource.id}
                  resource={resource}
                  onView={handleResourceClick}
                  onEdit={handleEditResource}
                  onDelete={handleDeleteResource}
                />
              ))}
            </CardGrid>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : resources.length === 0 ? (
            <div className="col-span-full text-center py-8">
              <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No resources found</h3>
            </div>
          ) : (
            resources.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onView={handleResourceClick}
                onEdit={handleEditResource}
                onDelete={handleDeleteResource}
              />
            ))
          )}
        </div>
      )}

      {/* Empty State */}
      {resources.length === 0 && (
        <div className="text-center py-12">
          <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No resources found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedType !== 'all' || selectedTags.length > 0
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first resource'
            }
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Resource
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <ResourceFormModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedResource(null);
        }}
        onSuccess={loadData}
        resource={selectedResource}
        mode={selectedResource ? 'edit' : 'create'}
      />

      {/* Resource Preview Modal */}
      <ResourcePreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedResource(null);
        }}
        resource={selectedResource}
      />
        </div>
      </div>
    </div>
  );
};
